import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Icon from '../Icon/Icon';
import Progressbar from '../Progressbar/Progressbar';

/**
 * `Button` is an extension of the HTML `button` element and makes it easier to display icons inside it using the *icon* or
 * *iconName* props. It also supports displaying a spinner to indicate that an operation is in progress.
 *
 * `Button` has styling for 3 different levels:
 * 1. "primary": Used for the main action in a UI. There should be only one primary button in each UI. Modal lightboxes and
 * dialogs may have their own primary buttons.
 * 2. "secondary": This is the default state and you don't have to provide this class.
 * 3. "tertiary": Used for buttons that are less important than standard buttons.
 *
 * In addition, you may use the class "core-icon-button" to get a rounded style to a button. This style is typically used for
 * icon buttons without text.
 *
 * @property {node} children Text or children of this button.
 * @property {string} className The class name of this button. Will be appended to "core-button".
 * @property {boolean} disabled Disables the button. Defaults to `false` (enabled).
 * @property {node} icon An optional SVG icon to show inside the button. Defaults to no icon.
 * @property {string} iconName The name of any icon in the vimond-core-component's icon library. See {@link Icon}.
 * @property {boolean} inProgress If set to `true`, a spinner will be displayed inside the button.
 *
 * @see Icon
 */
export default class Button extends PureComponent {

  static displayName = 'Button';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    disabled: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
    icon: PropTypes.node,
    iconName: PropTypes.string,
    inProgress: PropTypes.bool,
  };

  getDOMNode() {
    return this.refNode;
  }

  render() {
    const { children, className, icon, iconName, inProgress, ...rest } = this.props;

    const isRound = className && className.includes('core-icon-button');

    return (
      <button {...rest} className={cx('core-button', className, { 'in-progress': inProgress })} ref={c => { this.refNode = c; }}>
        {inProgress && <Progressbar circular={true} width={isRound ? 22 : 15} indeterminate/>}
        {(icon || iconName) && <Icon icon={icon} name={iconName}/>}
        {children}
      </button>
    );
  }

}
